import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";

// Soft UI Dashboard PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import RadarChart from "examples/Charts/RadarChart";
import Breadcrumbs from "examples/Breadcrumbs";

import radarChartData from "layouts/pages/charts/data/radarChartData";
import data from "./Questions";
import { call } from "services/axiosService/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useMaterialUIController } from "context";

import Loader from "pages/loader/Loader";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

export function Assessment() {
  const { t } = useTranslation();
  const matches = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const { id: assessment_id } = useParams();
  // const assessment = data.questions;
  const [assessment, setAssessment] = useState([]);
  const [assessmentType, setAssessmentType] = useState();
  const [options, setOptions] = useState([
    t("assessmentInstructions.options.disagree"),
    t("assessmentInstructions.options.slightlyDisagree"),
    t("assessmentInstructions.options.neutral"),
    t("assessmentInstructions.options.slightlyAgree"),
    t("assessmentInstructions.options.agree"),
  ]);
  const [currentQuestion, setCurrentQuestion] = useState(-1);
  const [answerList, setAnswerList] = useState([]);
  const [activeAssessment, setActiveAssessment] = useState({});
  const [clicked, setClicked] = useState(false);
  const [showInsightsLoader, setShowInsightsLoader] = useState(false);
  const val = (answerList.length / assessment.length) * 100;
  const [controller] = useMaterialUIController();
  const { profile } = controller;

  const answerbtnHandler = (ques, ans) => {
    setClicked(true);
    answerList[ques] = ans;
    setAnswerList(answerList);
    localStorage.setItem("answers" + assessment_id, JSON.stringify(answerList));
    const nextQuestion = currentQuestion + 1;
    if (nextQuestion <= assessment.length) {
      setTimeout(() => {
        setCurrentQuestion(nextQuestion);
        setClicked(false);
      }, 250);
    }
  };

  const handlePreviousQuestion = () => {
    const previousQuestion = currentQuestion - 1;
    if (previousQuestion < assessment.length + 1) {
      setCurrentQuestion(previousQuestion);
    }
  };

  const handleNextQuestion = () => {
    const nextQuestion = currentQuestion + 1;
    if (nextQuestion <= assessment.length) {
      setCurrentQuestion(nextQuestion);
    }
  };

  const submitBtnHandler = () => {
    const answers = localStorage.getItem("answers" + assessment_id);

    const body = {
      answers: answers,
      assigned_date: activeAssessment.assigned_date,
      details: {
        name: activeAssessment.details.name,
      },
      external_id: activeAssessment.external_id,
      id: activeAssessment.id,
      shared_with: activeAssessment.shared_with,
      status: "scoring",
      user_id: activeAssessment.user_id,
    };

    call(`users/${profile.id}/assessments/${assessment_id}`, "PUT", { body: body })
      .then((res) => {
        if (res.status == 200) {
          localStorage.removeItem("answers" + assessment_id);
          setShowInsightsLoader(true);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
  };

  useEffect(() => {
    try {
      const ansValue = JSON.parse(localStorage.getItem("answers" + assessment_id));
      if (!ansValue) {
        console.log("no answers found");
      } else {
        setAnswerList(ansValue);
      }
    } catch (err) {
      console.log(err);
    }
    call(`users/${profile.id}/assessments/${assessment_id}`, "GET", {})
      .then((res) => {
        if (res.status == 200) {
          setActiveAssessment(res.data);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (profile.id && assessment_id) {
      call(`users/${profile.id}/assessments/${assessment_id}/questions`, "GET", {})
        .then((res) => {
          if (res.status == 200) {
            setAssessment(res.data.questions);
            if (res.data.assessment_type == "SEARS") {
              setAssessmentType("SEARS");
              setOptions([
                t("assessmentInstructions.options.never"),
                t("assessmentInstructions.options.sometimes"),
                t("assessmentInstructions.options.often"),
                t("assessmentInstructions.options.always"),
              ]);
            } else {
              setAssessmentType("NEO");
            }
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          console.log(err, "err");
        });
    }
  }, []);

  const assessmentStartHandler = () => {
    setCurrentQuestion(answerList.length);
    if (activeAssessment.status == "not_started") {
      const body = {
        assigned_date: activeAssessment.assigned_date,
        details: {
          name: activeAssessment.details.name,
        },
        external_id: activeAssessment.external_id,
        shared_with: activeAssessment.shared_with,
        status: "in_progress",
      };

      call(`/users/${profile.id}/assessments/${assessment_id}`, "PUT", { body })
        .then((res) => {
          if (res.status == 200) {
            console.log(res.data);
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          console.log(err);
        });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar
        breadcrumbs={
          <Breadcrumbs
            icon="home"
            title={t("pageTitles.assessment")}
            route={["Assessments"]}
            light={false}
          />
        }
      />
      {showInsightsLoader && (
        <PreparingInsightsDialog
          assessmentId={assessment_id}
          userId={profile.id}
          showInsightsLoader={showInsightsLoader}
          setShowInsightsLoader={setShowInsightsLoader}
          t={t}
        />
      )}

      <MDBox my={3}>
        <Card sx={{ overflow: "hidden", minHeight: "72vh" }}>
          {/* Progress percentage + bar — visible only during questions */}
          {currentQuestion >= 0 && currentQuestion <= assessment.length && (
            <MDBox sx={{ px: 3, pt: 6 }}>
              <MDTypography
                variant="caption"
                sx={({ palette }) => ({
                  display: "block",
                  textAlign: "center",
                  color: palette.primary.main,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  mb: 0.5,
                  fontSize: "1.0rem",
                })}
              >
                {Math.round(val)}%
              </MDTypography>
              <MDBox
                sx={{ height: 4, bgcolor: "grey.200", borderRadius: "4px", position: "relative" }}
              >
                <MDBox
                  sx={({ palette }) => ({
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    borderRadius: "4px",
                    width: `${val}%`,
                    bgcolor: palette.primary.main,
                    transition: "width 400ms ease",
                  })}
                />
              </MDBox>
            </MDBox>
          )}

          {/* ── INSTRUCTIONS SCREEN ── */}
          {currentQuestion < 0 && (assessmentType === "NEO" || assessmentType === "SEARS") && (
            <Grid container sx={{ minHeight: "72vh" }}>
              {/* Left — welcome + instructions */}
              <Grid
                item
                xs={12}
                md={7}
                sx={{
                  p: { xs: 3, md: 6 },
                  display: "flex",
                  flexDirection: "column",
                  borderRight: { md: "1px solid" },
                  borderColor: { md: "grey.200" },
                }}
              >
                {/* Badge */}
                <MDTypography
                  variant="overline"
                  sx={({ palette }) => ({
                    color: palette.primary.main,
                    letterSpacing: "0.12em",
                    fontSize: "0.65rem",
                    fontStyle: "italic",
                    mb: 3,
                    display: "block",
                  })}
                >
                  {t("assessmentInstructions.yearlyInstruction")}
                </MDTypography>

                {/* Part 1 — big heading */}
                <MDTypography
                  variant="h3"
                  fontWeight="regular"
                  color="dark"
                  mb={2}
                  sx={{ lineHeight: 1.4, fontStyle: "italic" }}
                  dangerouslySetInnerHTML={{ __html: t("assessmentInstructions.title") }}
                />
                <MDTypography variant="body1" color="text" mb={3} sx={{ lineHeight: 1.7 }}>
                  {t("assessmentInstructions.instruction")}
                </MDTypography>

                <Divider sx={{ mb: 3, border: "1px solid", borderColor: "grey.800" }} />

                {/* Note in accent color */}
                <MDTypography
                  variant="body2"
                  sx={({ palette }) => ({ color: palette.primary.main, lineHeight: 1.7 })}
                  mb={3}
                >
                  {t("assessmentInstructions.note")}
                </MDTypography>

                <Divider sx={{ mt: 6, border: "1px solid", borderColor: "grey.800" }} />

                <MDBox mt="auto">
                  <MDTypography
                    variant="overline"
                    color="text"
                    sx={{ letterSpacing: "0.1em", fontSize: "0.8rem" }}
                  >
                    {t("assessmentInstructions.timeEstimate")}
                  </MDTypography>
                </MDBox>
              </Grid>

              {/* Right — scale + begin button */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  p: { xs: 3, md: 6 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <MDBox>
                  <MDTypography
                    variant="overline"
                    color="text"
                    sx={{ letterSpacing: "0.12em", fontSize: "0.65rem", display: "block", mb: 3 }}
                  >
                    {t("assessmentInstructions.scale")}
                  </MDTypography>

                  {options.map((opt, i) => (
                    <MDBox
                      key={i}
                      sx={{
                        py: 2,
                        borderBottom: "1px solid",
                        borderColor: "grey.500",
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                      }}
                    >
                      <MDTypography
                        variant="h4"
                        sx={({ palette }) => ({
                          color: palette.primary.main,
                          fontStyle: "italic",
                          minWidth: 24,
                          fontWeight: 400,
                        })}
                      >
                        {i + 1}
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={({ palette }) => ({
                          color: palette.black.main,
                        })}
                      >
                        {opt}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                <MDBox mt={5} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <MDButton color="primary" onClick={assessmentStartHandler}>
                    {activeAssessment.status === "not_started"
                      ? `${t("buttonTitles.beginAssessment")} →`
                      : `${t("buttonTitles.resumeAssessment")} →`}
                  </MDButton>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ letterSpacing: "0.1em", fontSize: "0.8rem" }}
                  >
                    {t("assessmentInstructions.thankYou")}
                  </MDTypography>
                </MDBox>
              </Grid>
            </Grid>
          )}

          {/* ── QUESTION SCREEN ── */}
          {currentQuestion > -1 && currentQuestion < assessment.length && (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: "72vh", p: { xs: 3, md: 6 } }}
            >
              {/* Question counter */}

              {/* <MDBox mb={2}>
                <MDTypography
                  component="span"
                  sx={({ palette }) => ({
                    color: palette.primary.main,
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    letterSpacing: "0.04em",
                  })}
                >
                  {t("assessmentInstructions.questionProgress", {
                    current: String(currentQuestion + 1).padStart(2, "0"),
                    total: assessment.length,
                  })}
                </MDTypography>
              </MDBox> */}

              {/* Question text */}
              <MDTypography
                variant="h4"
                fontWeight="regular"
                color="dark"
                textAlign="center"
                sx={{ maxWidth: 600, mb: 6, lineHeight: 1.6, fontStyle: "italic" }}
              >
                {assessment[currentQuestion]}
              </MDTypography>

              {/* Answer boxes */}
              <MDBox display="flex" gap={2} flexWrap="wrap" justifyContent="center" mb={6}>
                {options.map((ans, index) => {
                  const isSelected = answerList[currentQuestion] === index + 1;
                  return (
                    <MDBox
                      key={index}
                      onClick={() => answerbtnHandler(currentQuestion, index + 1)}
                      sx={({ palette }) => ({
                        width: { xs: 72, sm: 90 },
                        height: { xs: 80, sm: 95 },
                        border: "1.5px solid",
                        borderColor: isSelected ? palette.primary.dark : palette.primary.focus,
                        borderRadius: "12px",
                        bgcolor: isSelected ? palette.primary.dark : palette.primary.light,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        transition: "all 200ms ease",
                        "&:hover": {
                          borderColor: palette.primary.main,
                          bgcolor: palette.primary.focus,
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        },
                      })}
                    >
                      <MDTypography
                        variant="h5"
                        sx={({ palette }) => ({
                          color: isSelected ? "#ffffff" : palette.dark.main,
                          fontWeight: 700,
                          lineHeight: 1,
                        })}
                      >
                        {index + 1}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        textAlign="center"
                        sx={({ palette }) => ({
                          color: isSelected ? "#ffffffcc" : palette.text.main,
                          px: 0.5,
                          lineHeight: 1.2,
                          fontSize: "0.80rem",
                          fontWeight: 600,
                        })}
                      >
                        {ans}
                      </MDTypography>
                    </MDBox>
                  );
                })}
              </MDBox>

              {/* Previous / Next buttons */}
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestion === 0}
                >
                  ← {t("buttonTitles.previous")}
                </MDButton>

                <MDButton
                  variant="contained"
                  disabled={answerList.length <= currentQuestion}
                  onClick={() => answerList.length > currentQuestion && handleNextQuestion()}
                  sx={{
                    background: "#FF6B00",
                    color: "#ffffff",
                    "&:hover": { background: "#e05e00", filter: "none" },
                    "&:disabled": { background: "#ffb380", color: "#ffffff" },
                  }}
                >
                  {t("buttonTitles.next")} →
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          {/* ── COMPLETE SCREEN ── */}
          {currentQuestion === assessment.length && (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              sx={{ minHeight: "72vh", p: { xs: 3, md: 6 } }}
            >
              <MDTypography
                variant="overline"
                sx={({ palette }) => ({
                  color: palette.primary.main,
                  letterSpacing: "0.15em",
                  fontSize: "0.7rem",
                  mb: 3,
                  display: "block",
                })}
              >
                {t("assessmentInstructions.completed")}
              </MDTypography>

              <MDTypography
                variant="h3"
                fontWeight="regular"
                color="dark"
                mb={2}
                sx={{ lineHeight: 1.4, fontStyle: "italic" }}
                dangerouslySetInnerHTML={{
                  __html: t("assessmentInstructions.assessmentCompleted"),
                }}
              />

              <MDTypography
                variant="body2"
                color="text"
                mb={6}
                sx={{ maxWidth: 480, lineHeight: 1.8, fontWeight: 500, fontStyle: "italic" }}
              >
                {t("assessmentInstructions.finish")}
              </MDTypography>

              <MDButton color="primary" onClick={submitBtnHandler}>
                {t("buttonTitles.submitAssessment")} →
              </MDButton>
            </MDBox>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

const PreparingInsightsDialog = ({
  assessmentId,
  userId,
  setShowInsightsLoader,
  showInsightsLoader,
  t,
}) => {
  const navigate = useNavigate();
  const checkInsights = () => {
    call(`users/${userId}/assessments/${assessmentId}`, "GET", {})
      .then((res) => {
        if (res.status == 200) {
          const resData = res.data;
          // If Insights not present in assessment data again check after 10 seconds
          if (resData.status == "ready") {
            clearTimeout(myTimeout);
            clearInterval(myInterval);
            navigate(`/insight/${assessmentId}/${userId}`);
          }
        } else if (res.status == 440) {
          setShowInsightsLoader(false);
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
  };

  // Check Insights are ready or not for every 10 seconds
  let myInterval = setInterval(checkInsights, 10000);

  // After 60 seconds stop checking for insights
  let myTimeout = setTimeout(() => {
    setShowInsightsLoader(false);
    clearInterval(myInterval);
    navigate(`/insight/${assessmentId}/${userId}`);
  }, 60000);

  return (
    <Dialog open={showInsightsLoader} fullScreen>
      <DialogContent>
        <Loader loaderText={t("loaderText.scoringAssessment")} />
      </DialogContent>
    </Dialog>
  );
};
